import { useEffect, useMemo, useRef, useState } from 'react';

import { collection, getDocs, Query, QuerySnapshot, query, where } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import League, { LeagueStatus, LEAGUE_STATUSES } from '../types/League';
import Membership, { MEMBERSHIP_STATUSES } from '../types/Membership';
import Person from '../types/Person';

import useMembershipsOfPerson from './useMembershipsOfPerson';

export interface UseLeaguesProps {
  includeCatchAll?: boolean;
  includeLeagueAdmined?: boolean;
  includeOrgAdmined?: boolean;
  includeLeagueMember?: boolean;
  orgId?: string | null;
  person?: Person | null;
  status?: LeagueStatus[];
  providedMemberships?: {
    memberships: Membership[];
    isLoading: boolean;
    error?: Error;
  };
}

// IMPORTANT: The requester can only see leagues of another person if they are members in the same orgs.
// (god is the exception)
// This is because anyone in an org can see other org member's league memberships even if they are not in one of
// those leagues.
// But, you can't see memberships in orgs that you are not a member of. We enforce this by only ever
// querying the intersection of the target person's orgs and the requester's own orgs — the league read
// rule requires the requester themselves be a member/admin of the org, so querying an org outside that
// intersection would just be a doomed request.
export default function useLeagues(props: UseLeaguesProps) {
  // Memoize props to prevent unnecessary re-renders
  const memoizedProps = useMemo(() => props, [props]);
  const {
    includeCatchAll = false,
    includeLeagueAdmined = false, // if true, include all leagues in membership.leagueAdmins[]
    includeOrgAdmined = false, // if true, include all leagues in orgs in membership.orgAdmins[]
    includeLeagueMember = true, // if true, include all leagues in membership.leagues[]
    orgId,
    person,
    status,
    providedMemberships,
  } = memoizedProps;
  const { isGod } = useAuth();
  const { userState, getAllActiveOrgs, getAllPendingOrgs } = useData();

  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLeaguesLoading, setIsLeaguesLoading] = useState(true);
  const [leaguesError, setLeaguesError] = useState<Error | undefined>(undefined);
  const fetchAttemptRef = useRef(0);
  const lastFetchKeyRef = useRef<string>('');

  // Always call the hook (React rules) but prevent query when we have provided memberships
  const queriedMembershipData = useMembershipsOfPerson({
    profileKeys: userState.profileKeys,
    personId: providedMemberships ? null : (person?.itemId ?? null), // Don't query if we have provided memberships
    orgId,
    status: MEMBERSHIP_STATUSES.ACTIVE,
  });

  // Use provided memberships if available, otherwise use queried data.
  // The query is already scoped by orgId; provided memberships span every org, so scope them here.
  const memberships = useMemo(() => {
    const source = providedMemberships?.memberships ?? queriedMembershipData.memberships;
    return orgId && source ? source.filter((m) => m.orgId === orgId) : source;
  }, [providedMemberships?.memberships, queriedMembershipData.memberships, orgId]);
  const isMembershipsLoading = providedMemberships?.isLoading ?? queriedMembershipData.isMembershipsLoading;
  const membershipsError = providedMemberships?.error ?? queriedMembershipData.membershipsError;

  // Create a stable key for memberships to avoid unnecessary re-fetches
  const membershipsKey = useMemo(() => {
    if (!memberships) return 'no-memberships';
    return memberships.map(m => `${m.orgId}-${m.itemId}`).sort().join(',');
  }, [memberships]);

  useEffect(() => {
    let cancelled = false;
    const currentAttempt = ++fetchAttemptRef.current;

    // Create a key for this fetch based on dependencies
    const fetchKey = `${membershipsKey}-${person?.itemId}-${orgId}-${includeCatchAll}-${includeLeagueAdmined}-${includeOrgAdmined}-${includeLeagueMember}-${status?.join(',')}`;

    // If we've already fetched for this key, don't fetch again
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }

    // Early exit conditions
    if (userState.status === 'logged_out') {
      setLeagues([]);
      setIsLeaguesLoading(false);
      setLeaguesError(new Error('User must be logged in to view leagues'));
      lastFetchKeyRef.current = fetchKey;
      return;
    }

    if (membershipsError) {
      setLeagues([]);
      setIsLeaguesLoading(false);
      setLeaguesError(membershipsError);
      lastFetchKeyRef.current = fetchKey;
      return;
    }

    if (person === null) {
      setLeagues([]);
      setIsLeaguesLoading(false);
      setLeaguesError(undefined);
      lastFetchKeyRef.current = fetchKey;
      return;
    }

    if (userState.status !== 'ready' || !person || !memberships || isMembershipsLoading) {
      setIsLeaguesLoading(true);
      return;
    }

    // Build league IDs set based on conditions
    const buildLeagueIds = (): string[] => {
      if (!memberships) return [];

      // Filter memberships by active/pending status.
      // No need to cross-reference person.orgIds — the memberships themselves
      // are the authoritative source of which orgs the person belongs to.
      const relevantMemberships = memberships.filter(
        (m) =>
          m.orgId &&
          (m.status === MEMBERSHIP_STATUSES.ACTIVE ||
           m.status === MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE ||
           m.status === MEMBERSHIP_STATUSES.PENDING)
      );

      const leagueIdSet = new Set<string>();

      relevantMemberships.forEach((membership) => {
        const leaguesToAdd: string[] = [];
        const leagueAdminsToAdd: string[] = [];

        // Collect leagues based on conditions
        // Use adminedLeagueIdsImplicit for org admin leagues (implicit admin rights)
        if (includeOrgAdmined && membership.isAdmin) {
          if (membership.leagues) leaguesToAdd.push(...membership.leagues);
          if (membership.adminedLeagueIdsImplicit) leagueAdminsToAdd.push(...membership.adminedLeagueIdsImplicit);
        }

        // Use both explicit and implicit for league admin check
        if (includeLeagueAdmined) {
          if (membership.adminedLeagueIdsExplicit) leagueAdminsToAdd.push(...membership.adminedLeagueIdsExplicit);
          if (membership.adminedLeagueIdsImplicit) leagueAdminsToAdd.push(...membership.adminedLeagueIdsImplicit);
        }

        if (includeLeagueMember && membership.leagues) {
          leaguesToAdd.push(...membership.leagues);
        }

        // Merge into main set, filtering out empty strings
        [...leaguesToAdd, ...leagueAdminsToAdd].forEach((id) => {
          if (id) leagueIdSet.add(id);
        });
      });

      return Array.from(leagueIdSet);
    };

    const fetchLeagues = async () => {
      try {
        setIsLeaguesLoading(true);
        setLeaguesError(undefined);

        const leagueIds = buildLeagueIds();

        if (leagueIds.length === 0) {
          setLeagues([]);
          setIsLeaguesLoading(false);
          lastFetchKeyRef.current = fetchKey;
          return;
        }

        // Extract unique orgIds from memberships
        const orgIdSet = new Set<string>();
        memberships?.forEach((m) => {
          if (m.orgId) {
            orgIdSet.add(m.orgId);
          }
        });
        const orgIds = Array.from(orgIdSet);

        if (orgIds.length === 0) {
          setLeagues([]);
          setIsLeaguesLoading(false);
          lastFetchKeyRef.current = fetchKey;
          return;
        }

        // The league read rule requires the requester themselves be a member/admin of the org,
        // so only query orgs the requester can actually read from — querying an org outside that
        // set would just be a doomed request. God bypasses the rule, so skip the intersection for them.
        const requesterOrgIds = new Set([...getAllActiveOrgs(), ...getAllPendingOrgs()]);
        const queryableOrgIds = isGod ? orgIds : orgIds.filter((id) => requesterOrgIds.has(id));

        if (queryableOrgIds.length === 0) {
          setLeagues([]);
          setIsLeaguesLoading(false);
          lastFetchKeyRef.current = fetchKey;
          return;
        }

        // Build base conditions (same for all queries)
        const baseConditions: ReturnType<typeof where>[] = [];

        if (!includeCatchAll) {
          baseConditions.push(where('isCatchAll', '==', false));
        }

        if (status && status.length > 0) {
          baseConditions.push(where('status', 'in', status));
        } else {
          baseConditions.push(where('status', 'in', [LEAGUE_STATUSES.ACTIVE, LEAGUE_STATUSES.ARCHIVED]));
        }

        // Create a Set of league IDs for fast lookup
        const leagueIdSet = new Set(leagueIds);

        // Query each org's league subcollection directly (path already scopes by orgId)
        // Batch in groups of 5 to avoid overwhelming Firestore
        const BATCH_SIZE = 5;
        const results: QuerySnapshot<League>[] = [];
        for (let i = 0; i < queryableOrgIds.length; i += BATCH_SIZE) {
          const batch = queryableOrgIds.slice(i, i + BATCH_SIZE);
          const batchResults = await Promise.all(
            batch.map((queryableOrgId) =>
              getDocs(
                query(
                  collection(firestore, `org/${queryableOrgId}/league`) as Query<League>,
                  ...baseConditions
                )
              )
            )
          );
          if (cancelled) return;
          results.push(...batchResults);
        }

        // Combine results from all queries and filter by league IDs
        const allLeagues: League[] = [];
        const seenIds = new Set<string>();

        results.forEach((snapshot) => {
          snapshot.docs.forEach((doc) => {
            const league = doc.data() as League;
            // Only include leagues that are in our league IDs set
            if (league.itemId && leagueIdSet.has(league.itemId) && !seenIds.has(league.itemId)) {
              seenIds.add(league.itemId);
              allLeagues.push(league);
            }
          });
        });

        // Sort by name
        allLeagues.sort((a, b) => a.name.localeCompare(b.name));

        if (!cancelled && fetchAttemptRef.current === currentAttempt) {
          setLeagues(allLeagues);
          setIsLeaguesLoading(false);
          lastFetchKeyRef.current = fetchKey;
        }
      } catch (error) {
        console.error('Error fetching leagues:', error);
        if (!cancelled && fetchAttemptRef.current === currentAttempt) {
          setLeaguesError(error as Error);
          setIsLeaguesLoading(false);
          lastFetchKeyRef.current = fetchKey;
        }
      }
    };

    fetchLeagues();

    return () => {
      cancelled = true;
    };
  }, [
    getAllActiveOrgs,
    getAllPendingOrgs,
    includeCatchAll,
    includeLeagueAdmined,
    includeOrgAdmined,
    includeLeagueMember,
    isGod,
    membershipsKey,
    memberships,
    orgId,
    person?.itemId,
    userState.status,
    status,
    isMembershipsLoading,
    membershipsError,
  ]);

  // Authorization check
  if (
    userState.status === 'ready' &&
    userState.userProfile &&
    !leagues &&
    !isLeaguesLoading &&
    !leaguesError
  ) {
    return {
      leagues: [],
      isLeaguesLoading: false,
      leaguesError: new Error('Not authorized to view these leagues'),
    };
  }

  return {
    leagues: leagues || [],
    isLeaguesLoading:
      isLeaguesLoading ||
      userState.status === 'authenticating_user' ||
      userState.status === 'authenticating_profile' ||
      userState.status === 'authenticating_memberships' ||
      isMembershipsLoading,
    leaguesError: leaguesError || membershipsError,
  };
}
